import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../Layout/Layout';
import styles from './UserEmailInbox.module.css';
import { URL as BASEURL } from '../AxiosUrlGlobal';
import EmailDetailModal from './EmailDetailModal';
import ComposeEmailModal from './ComposeEmailModal';
import { 
    HiOutlineEye, 
    HiOutlineCursorClick, 
    HiOutlineBan,
    HiOutlinePaperAirplane,
    HiOutlineTrash
} from 'react-icons/hi';
import { FiPaperclip } from 'react-icons/fi';

/**
 * UserEmailInbox - Email Inbox for Users
 * View emails from user's domains with pagination, compose, and reply functionality
 */
function UserEmailInbox() {
    const [emails, setEmails] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [selectedThread, setSelectedThread] = useState(null);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showComposeModal, setShowComposeModal] = useState(false);

    // Stats
    const [stats, setStats] = useState({
        total: 0,
        unread: 0,
        today: 0,
        sent: 0,
        opened: 0,
        clicked: 0,
        unsubscribed: 0
    });
    const [deletingEmails, setDeletingEmails] = useState(new Set()); // Track emails being deleted
    const [deletingAll, setDeletingAll] = useState(false); // Track if deleting all emails

    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0,
        limit: 20,
        hasPrevPage: false,
        hasNextPage: false
    });

    const [filters, setFilters] = useState({
        search: '',
        email: '',
        status: 'all' // all, unread, read
    });

    const userId = localStorage.getItem('id');
    const token = localStorage.getItem('token');

    useEffect(() => {
        fetchEmails();
    }, [pagination.currentPage, filters]);

    useEffect(() => {
        fetchAccounts();
    }, []);

    const fetchAccounts = async () => {
        try {
            const response = await axios.get(
                `${BASEURL}user/email-accounts/${userId}?limit=100`,
                { headers: { token } }
            );

            if (response.data.success) {
                setAccounts(response.data.data.accounts || []);
            }
        } catch (err) {
            console.error('Error fetching email accounts:', err);
        }
    };

    const fetchEmails = async () => {
        try {
            setLoading(true);
            setError('');

            const queryParams = new URLSearchParams({
                page: pagination.currentPage,
                limit: pagination.limit,
                ...(filters.email && { email: filters.email }),
                ...(filters.search && { search: filters.search }),
                ...(filters.status !== 'all' && { status: filters.status })
            });

            const response = await axios.get(
                `${BASEURL}user/emails/${userId}?${queryParams}`,
                { headers: { token } }
            );

            if (response.data.success) {
                setEmails(response.data.data.emails);
                setPagination(response.data.data.pagination);
                setStats(response.data.data.stats || { total: 0, unread: 0, today: 0 });
            } else {
                setError(response.data.error || 'Failed to load emails');
            }
        } catch (err) {
            console.error('Error fetching emails:', err);
            setError(err.response?.data?.error || 'Failed to load emails');
        } finally {
            setLoading(false);
        }
    };

    const handleEmailClick = async (emailOrThread) => {
        try {
            // URL encode the emailId to handle special characters and dots
            const emailId = encodeURIComponent(emailOrThread.id);
            const response = await axios.get(
                `${BASEURL}user/email/${userId}?emailId=${emailId}`,
                { headers: { token } }
            );

            if (response.data.success) {
                setSelectedEmail(response.data.data.email);
                // Pass thread info if this is a conversation
                setSelectedThread(emailOrThread.messageCount > 1 ? emailOrThread : null);
                setShowDetailModal(true);
            } else {
                alert('Failed to load email details');
            }
        } catch (err) {
            console.error('Error fetching email details:', err);
            alert('Failed to load email details');
        }
    };

    const handleEmailDelete = async (emailIdOrFilename, event) => {
        // Prevent event bubbling to avoid opening the email
        if (event) {
            event.stopPropagation();
        }
        
        // Normalize the email ID for tracking
        let normalizedId = emailIdOrFilename;
        if (emailIdOrFilename && emailIdOrFilename.startsWith('sent-')) {
            normalizedId = emailIdOrFilename.replace(/^sent-/, '');
        }
        
        // Check if this email is already being deleted
        if (deletingEmails.has(normalizedId) || deletingEmails.has(emailIdOrFilename)) {
            console.log('Email deletion already in progress:', emailIdOrFilename);
            return;
        }
        
        // Confirm deletion
        if (!window.confirm('Are you sure you want to delete this email?')) {
            return;
        }
        
        // Mark as being deleted
        setDeletingEmails(prev => new Set([...prev, normalizedId, emailIdOrFilename]));
        
        try {
            // Call the backend API to delete the email
            // Backend expects emailId as query parameter
            const emailId = encodeURIComponent(emailIdOrFilename);
            const response = await axios.delete(
                `${BASEURL}user/email/${userId}?emailId=${emailId}`,
                { headers: { token } }
            );

            if (response.data.success) {
                // Remove "sent-" prefix if present for matching
                let emailIdToMatch = emailIdOrFilename;
                if (emailIdOrFilename && emailIdOrFilename.startsWith('sent-')) {
                    emailIdToMatch = emailIdOrFilename.replace(/^sent-/, '');
                }
                
                // Optimistically remove from UI
                setEmails(prevEmails => {
                    return prevEmails.filter(e => {
                        // Direct match on id or filename
                        if (e.id === emailIdOrFilename || e.filename === emailIdOrFilename) {
                            return false; // Remove this email/thread
                        }
                        // Match without "sent-" prefix
                        if (e.id === emailIdToMatch || e.filename === emailIdToMatch) {
                            return false; // Remove this email/thread
                        }
                        // For sent emails, check if filename matches
                        if (e.filename && e.filename.startsWith('sent-')) {
                            const emailIdFromFilename = e.filename.replace(/^sent-/, '');
                            if (emailIdFromFilename === emailIdToMatch || emailIdFromFilename === emailIdOrFilename) {
                                return false; // Remove this email/thread
                            }
                        }
                        // Check messageIds in threads (for both sent and received emails)
                        if (e.messageIds && Array.isArray(e.messageIds)) {
                            const hasMatchingMessage = e.messageIds.some(msg => {
                                const msgId = msg.id || '';
                                const msgFilename = msg.filename || '';
                                return msgId === emailIdOrFilename || 
                                       msgId === emailIdToMatch ||
                                       msgFilename === emailIdOrFilename ||
                                       msgFilename === emailIdToMatch ||
                                       (typeof msgId === 'string' && (msgId.includes(emailIdToMatch) || msgId.includes(emailIdOrFilename)));
                            });
                            if (hasMatchingMessage) {
                                // If thread has only one message, remove entire thread
                                if (!e.messageCount || e.messageCount <= 1) {
                                    return false;
                                }
                                // For threads with multiple messages, the refresh will handle it
                            }
                        }
                        return true; // Keep this email/thread
                    });
                });
                
                // Update stats
                setStats(prev => ({
                    ...prev,
                    total: Math.max(0, prev.total - 1)
                }));
                
                // Refresh the list to get accurate data from server
                setTimeout(async () => {
                    try {
                        await fetchEmails();
                    } catch (err) {
                        console.error('Error refreshing emails after delete:', err);
                    } finally {
                        // Remove from deleting set after refresh
                        setDeletingEmails(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(normalizedId);
                            newSet.delete(emailIdOrFilename);
                            return newSet;
                        });
                    }
                }, 500);
            } else {
                alert('Failed to delete email: ' + (response.data.error || 'Unknown error'));
                // Remove from deleting set on failure
                setDeletingEmails(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(normalizedId);
                    newSet.delete(emailIdOrFilename);
                    return newSet;
                });
            }
        } catch (err) {
            console.error('Error deleting email:', err);
            alert('Failed to delete email: ' + (err.response?.data?.error || err.message || 'Unknown error'));
            // Remove from deleting set on error
            setDeletingEmails(prev => {
                const newSet = new Set(prev);
                newSet.delete(normalizedId);
                newSet.delete(emailIdOrFilename);
                return newSet;
            });
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const formatDate = (date) => {
        const d = new Date(date);
        const now = new Date();
        const diff = now - d;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (days === 0) {
            return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else if (days === 1) {
            return 'Yesterday';
        } else if (days < 7) {
            return `${days} days ago`;
        } else {
            return d.toLocaleDateString();
        }
    };

    const formatSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const getFromDisplay = (from) => {
        if (!from) return 'Unknown';
        if (from.name) {
            return `${from.name} <${from.address}>`;
        }
        return from.address || 'Unknown';
    };

    const handleComposeSent = () => {
        setShowComposeModal(false);
        fetchEmails(); // Refresh emails
    };

    const handleDeleteAll = async () => {
        // Get total count of emails matching current filters
        const totalEmails = pagination.totalRecords;
        
        if (totalEmails === 0) {
            alert('No emails to delete');
            return;
        }

        // Confirm deletion
        const confirmMessage = `Are you sure you want to delete ALL ${totalEmails} email(s)${filters.search || filters.email || filters.status !== 'all' ? ' matching current filters' : ''}?\n\nThis action cannot be undone.`;
        if (!window.confirm(confirmMessage)) {
            return;
        }

        setDeletingAll(true);
        setError('');

        try {
            // Fetch all emails across all pages to get their IDs
            const allEmailIds = [];
            let currentPage = 1;
            let hasMorePages = true;

            // Fetch all pages to get all email IDs
            while (hasMorePages) {
                const queryParams = new URLSearchParams({
                    page: currentPage,
                    limit: pagination.limit,
                    ...(filters.email && { email: filters.email }),
                    ...(filters.search && { search: filters.search }),
                    ...(filters.status !== 'all' && { status: filters.status })
                });

                const response = await axios.get(
                    `${BASEURL}user/emails/${userId}?${queryParams}`,
                    { headers: { token } }
                );

                if (response.data.success && response.data.data.emails) {
                    const pageEmails = response.data.data.emails;
                    pageEmails.forEach(email => {
                        const emailId = email.filename || email.id;
                        if (emailId && !allEmailIds.includes(emailId)) {
                            allEmailIds.push(emailId);
                        }
                    });

                    const paginationInfo = response.data.data.pagination;
                    hasMorePages = paginationInfo.hasNextPage;
                    currentPage++;
                } else {
                    hasMorePages = false;
                }
            }

            if (allEmailIds.length === 0) {
                alert('No emails found to delete');
                setDeletingAll(false);
                return;
            }

            // Delete all emails with concurrency limit (5 at a time)
            const deleteConcurrency = 5;
            let deletedCount = 0;
            let failedCount = 0;

            for (let i = 0; i < allEmailIds.length; i += deleteConcurrency) {
                const batch = allEmailIds.slice(i, i + deleteConcurrency);
                
                await Promise.all(
                    batch.map(async (emailId) => {
                        try {
                            const encodedEmailId = encodeURIComponent(emailId);
                            const response = await axios.delete(
                                `${BASEURL}user/email/${userId}?emailId=${encodedEmailId}`,
                                { headers: { token } }
                            );

                            if (response.data.success) {
                                deletedCount++;
                            } else {
                                failedCount++;
                                console.error(`Failed to delete email ${emailId}:`, response.data.error);
                            }
                        } catch (err) {
                            failedCount++;
                            console.error(`Error deleting email ${emailId}:`, err);
                        }
                    })
                );
            }

            // Clear the current emails list immediately
            setEmails([]);
            
            // Show result message
            if (failedCount === 0) {
                alert(`Successfully deleted ${deletedCount} email(s)`);
            } else {
                alert(`Deleted ${deletedCount} email(s), ${failedCount} failed`);
            }

            // Reset to page 1 and refresh the email list
            setPagination(prev => ({ ...prev, currentPage: 1 }));
            // Small delay to ensure backend has processed all deletions
            await new Promise(resolve => setTimeout(resolve, 1500));
            await fetchEmails();

        } catch (err) {
            console.error('Error deleting all emails:', err);
            setError(err.response?.data?.error || 'Failed to delete all emails');
            alert('Failed to delete all emails: ' + (err.response?.data?.error || err.message || 'Unknown error'));
        } finally {
            setDeletingAll(false);
        }
    };

    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.header}>
                <div>
                    <h1>Email Inbox</h1>
                    <p>View and manage emails from your domains</p>
                </div>
                <div className={styles.headerActions}>
                    <button
                        className={styles.deleteAllButton}
                        onClick={handleDeleteAll}
                        disabled={deletingAll || pagination.totalRecords === 0 || loading}
                        title={pagination.totalRecords === 0 ? 'No emails to delete' : 'Delete all emails'}
                    >
                        <HiOutlineTrash className={styles.deleteAllIcon} />
                        {deletingAll ? 'Deleting...' : 'Delete All'}
                    </button>
                    <button
                        className={styles.composeButton}
                        onClick={() => setShowComposeModal(true)}
                        disabled={accounts.length === 0}
                    >
                        + Compose Email
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className={styles.statsContainer}>
                <div className={styles.statCard}>
                    <div className={styles.statNumber}>{stats.total}</div>
                    <div className={styles.statLabel}>Total Emails</div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statNumber}>{stats.unread}</div>
                    <div className={styles.statLabel}>Unread</div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statNumber}>{stats.today}</div>
                    <div className={styles.statLabel}>Today</div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statNumber}>{stats.sent || 0}</div>
                    <div className={styles.statLabel}>Sent</div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statNumber}>{stats.opened || 0}</div>
                    <div className={styles.statLabel}>Opened</div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statNumber}>{stats.clicked || 0}</div>
                    <div className={styles.statLabel}>Clicked</div>
                </div>
                <div className={styles.statCard}>
                    <div className={styles.statNumber}>{stats.unsubscribed || 0}</div>
                    <div className={styles.statLabel}>Unsubscribed</div>
                </div>
            </div>

            {/* Filters */}
            <div className={styles.filters}>
                <input
                    type="text"
                    placeholder="Search emails..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className={styles.searchInput}
                />
                <select
                    value={filters.email}
                    onChange={(e) => handleFilterChange('email', e.target.value)}
                    className={styles.filterSelect}
                >
                    <option value="">All Accounts</option>
                    {accounts.map((account) => (
                        <option key={account._id} value={account.email}>
                            {account.email}
                        </option>
                    ))}
                </select>
                <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className={styles.filterSelect}
                >
                    <option value="all">All Status</option>
                    <option value="unread">Unread Only</option>
                    <option value="read">Read Only</option>
                    <option value="sent">Sent Only</option>
                    <option value="received">Received Only</option>
                    <option value="opened">Opened</option>
                    <option value="clicked">Clicked</option>
                    <option value="unsubscribed">Unsubscribed</option>
                </select>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            {accounts.length === 0 && !loading && (
                <div className={styles.warning}>
                    You don't have any email accounts yet. Please create an email account first in Email Management.
                </div>
            )}

            {loading ? (
                <div className={styles.emailList}>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                        <div key={i} className={styles.skeletonEmailItem}>
                            <div className={styles.skeletonEmailHeader}>
                                <div className={styles.skeletonEmailFrom}>
                                    <div className={styles.skeleton} style={{width: '180px', height: '16px'}}></div>
                                    <div className={styles.skeleton} style={{width: '30px', height: '18px', borderRadius: '12px'}}></div>
                                </div>
                                <div className={styles.skeletonEmailMeta}>
                                    <div className={styles.skeleton} style={{width: '65px', height: '14px'}}></div>
                                    <div className={styles.skeleton} style={{width: '40px', height: '14px'}}></div>
                                    <div className={styles.skeleton} style={{width: '24px', height: '24px', borderRadius: '4px'}}></div>
                                </div>
                            </div>
                            <div className={styles.skeleton} style={{width: '70%', height: '16px', marginBottom: '8px'}}></div>
                            <div className={styles.skeletonEmailAccount}>
                                <div className={styles.skeleton} style={{width: '130px', height: '22px', borderRadius: '4px'}}></div>
                                <div className={styles.skeleton} style={{width: '55px', height: '22px', borderRadius: '4px'}}></div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : emails.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No emails found</p>
                    <p className={styles.emptyHint}>
                        {filters.search || filters.email || filters.status !== 'all'
                            ? 'Try adjusting your filters'
                            : 'No emails in your mailboxes'}
                    </p>
                </div>
            ) : (
                <>
                    {/* Email List (Threads) */}
                    <div className={styles.emailList}>
                        {emails.map((email) => (
                            <div
                                key={email.id}
                                className={`${styles.emailItem} ${!email.isRead || email.unreadCount > 0 ? styles.unread : ''}`}
                                onClick={() => handleEmailClick(email)}
                            >
                                <div className={styles.emailHeader}>
                                    <div className={styles.emailFrom}>
                                        <strong>{getFromDisplay(email.lastFrom || email.from)}</strong>
                                        {email.messageCount > 1 && (
                                            <span className={styles.threadCount}>({email.messageCount})</span>
                                        )}
                                        {email.unreadCount > 0 && (
                                            <span className={styles.unreadBadge}>
                                                {email.unreadCount} new
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.emailMeta}>
                                        <span className={styles.emailDate}>{formatDate(email.lastDate || email.date)}</span>
                                        {email.hasAttachments && (
                                            <span className={styles.attachmentIcon}><FiPaperclip /></span>
                                        )}
                                        <span className={styles.emailSize}>{formatSize(email.totalSize || email.size)}</span>
                                        <button
                                            className={styles.deleteButton}
                                            onClick={(e) => handleEmailDelete(email.filename || email.id, e)}
                                            title="Delete email"
                                            aria-label="Delete email"
                                        >
                                            <HiOutlineTrash className={styles.deleteIcon} />
                                        </button>
                                    </div>
                                </div>
                                <div className={styles.emailSubject}>
                                    {email.subject}
                                    {email.messageCount > 1 && (
                                        <span className={styles.threadIndicator}> — Conversation</span>
                                    )}
                                </div>
                                <div className={styles.emailAccount}>
                                    <span className={styles.accountBadge}>{email.accountEmail}</span>
                                    {email.isSent && (
                                        <span className={styles.sentBadge}>
                                            <HiOutlinePaperAirplane className={styles.badgeIcon} /> Sent
                                        </span>
                                    )}
                                    {email.tracking && (
                                        <div className={styles.trackingMetrics}>
                                            {email.tracking.opened && (
                                                <span className={styles.trackingBadge}>
                                                    <HiOutlineEye className={styles.badgeIcon} />
                                                    {email.tracking.openCount}
                                                </span>
                                            )}
                                            {email.tracking.clicked && (
                                                <span className={styles.trackingBadge}>
                                                    <HiOutlineCursorClick className={styles.badgeIcon} />
                                                    {email.tracking.clickCount}
                                                </span>
                                            )}
                                            {email.tracking.unsubscribed && (
                                                <span className={styles.trackingBadgeUnsubscribed}>
                                                    <HiOutlineBan className={styles.badgeIcon} />
                                                    Unsubscribed
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {email.messageCount > 1 && email.messageIds && (
                                        <span className={styles.participantsHint}>
                                            {[...new Set(email.messageIds.map(m => m.from?.name || m.from?.address))].slice(0, 3).join(', ')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className={styles.pagination}>
                            <button
                                disabled={!pagination.hasPrevPage}
                                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                            >
                                Previous
                            </button>
                            <span>
                                Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalRecords} emails)
                            </span>
                            <button
                                disabled={!pagination.hasNextPage}
                                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Email Detail Modal */}
            {showDetailModal && selectedEmail && (
                <EmailDetailModal
                    email={selectedEmail}
                    thread={selectedThread}
                    onClose={() => {
                        setShowDetailModal(false);
                        setSelectedEmail(null);
                        setSelectedThread(null);
                        // Refresh emails to update read status
                        fetchEmails();
                    }}
                    onDelete={handleEmailDelete}
                    userId={userId}
                    token={token}
                />
            )}

            {/* Compose Email Modal */}
            {showComposeModal && (
                <ComposeEmailModal
                    accounts={accounts}
                    onClose={() => setShowComposeModal(false)}
                    onSent={handleComposeSent}
                    userId={userId}
                    token={token}
                />
            )}
        </div>
    );
}

export default UserEmailInbox;
